import { ReactNode, useEffect, useId, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
    Action,
    BusinessRule,
    BusinessRuleRuntimeStatus,
    EmailNotification,
    from,
    is,
    Project,
    PushNotification,
} from '@the-real-insight/tri-model';
import { userAPI } from '../UserAPI';
import { sendTestPush } from '../PushAPI';

import { CENTERED_PAGE_CLASS } from '../components/pageLayout';
import { deviceAlarmsOf } from './alertDeviceAlarms';
import { noUplinkDays, noUplinkIsCombined } from './noUplink';
import {
    CooldownUnit,
    cooldownMinutesOf,
    cooldownToFields,
    parseCooldown,
    withCooldownMinutes,
} from './alertCooldown';
import { useModalContext } from '../components/ModalProvider';
import { alertSampleContext, ALERT_TEXT_NAMESPACE, renderAlertTemplate } from './alertPlaceholders';
import { isAllSelected, selectionForSave, selectionFromRule } from './alertScopeSelection';
import { recipientCount, scopeCountsOf, ScopeCounts, summarizeConditions } from './alertRuleSummary';
import AlertConditionsEditor from './AlertConditionsEditor';
import AlertPresetChooser from './AlertPresetChooser';
import AlertSentence from './AlertSentenceView';
import StickyHeader from './StickyHeader';
import { actionIsEmpty, defaultAction } from './alertDefaults';
import { AlertPreset } from './alertPresets';
import { buildSentence, operatorWords } from './alertSentence';
import ActionEditor, {
    Translate,
    unknownPlaceholderMessage,
    unknownPlaceholdersOfAction,
} from './AlertActionEditor';
import { UnknownPlaceholder } from './alertPlaceholders';
import PushActionEditor from './AlertPushActionEditor';
import { usePushUsers } from './usePushUsers';
import { useOwnRoles } from './useOwnRoles';
import { ownPushStateOf } from './pushRecipients';
import { useGuideAction } from '../guide/useGuideAction';
import { authenticationAtom } from '../State';
import { useAtom } from 'jotai';
import { useUrlPageState } from '../utilities/dataTablePagination';

import type { Column, ColumnDef } from '@tanstack/react-table';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Bell,
    ChevronRight,
    ChevronsUpDown,
    Loader2,
    Mail,
    MessageSquarePlus,
    Plus,
    Power,
    PowerOff,
    RefreshCw,
    Search,
    Trash2,
} from 'lucide-react';

import { BRAND } from '@/brand/brand';
import { withAlertLanguage } from './alertLanguage';
import { withPortalRootURL } from './alertPortalRoot';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/sonner';

interface Properties {
}

const TEXT = ALERT_TEXT_NAMESPACE;

/** Which part of the fleet a rule is limited to; "all devices" when it is limited to none. */
const scopeLabel = (counts: ScopeCounts, t: Translate): string => {
    const parts: string[] = [];

    if (counts.projects !== null) {
        parts.push(counts.projects === 1 ? t(`${TEXT}scopeProjectOne`) : t(`${TEXT}scopeProjects`, { n: String(counts.projects) }));
    }

    if (counts.products !== null) {
        parts.push(counts.products === 1 ? t(`${TEXT}scopeProductOne`) : t(`${TEXT}scopeProducts`, { n: String(counts.products) }));
    }

    return parts.length > 0 ? parts.join(' · ') : t(`${TEXT}scopeAll`);
};

interface AlertPanelProperties {
    alert: BusinessRule;
    setAlert: (alert: BusinessRule | null) => void;
    t: Translate;
    language: string;
}

interface MultiSelectOption {
    key: string;
    text: string;
}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                : sorted === 'desc'
                    ? <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                    : <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />}
        </Button>
    );
}

/** Leerzustand der Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

const formatDateTime = (value: any) =>
    value ? `${new Date(value).toLocaleDateString()} ${new Date(value).toLocaleTimeString()}` : '-';

/**
 * Mehrfachauswahl mit „Alle“-Eintrag.
 *
 * „Alle“ ist die **leere** Liste — siehe `alertScopeSelection`: So liest der Server sie (keine
 * Einschränkung), und nur so ist ein morgen angelegtes Projekt miterfasst. Das frühere Sentinel
 * `_all` neben jeder Id wird nicht mehr geschrieben; steht es in einer Bestandsregel, zeigt der
 * Picker die Ids, die daneben stehen (#1225).
 *
 * Die leere Liste schreiben nur der „Alle“-Eintrag und das abgewählte letzte Häkchen. Kreuzt jemand
 * jeden Eintrag einzeln an, bleibt die Aufzählung stehen und der Auslesetext nennt die Namen: Beim
 * Server heißt leer „ohne Einschränkung“ und erfasst auch die Assets ohne Projekt und ohne Produkt
 * — das ist etwas anderes als „die fünf, die ich angekreuzt habe“.
 *
 * Im Zustand „Alle“ ist deshalb **nur** die „Alle“-Zeile angekreuzt, die Einzelkästchen sind leer:
 * Genau das steht in der Regel. Sie alle anzukreuzen sähe nach demselben Fokus aus, machte aber
 * jeden Klick zum Gegenteil dessen, was er meint — wer einen Eintrag ankreuzt, will auf ihn
 * einschränken und nicht ihn ausnehmen; Einschränken auf einen Eintrag kostete N−1 Klicks, und bei
 * einer einzigen Option wäre es gar nicht möglich. Ein Klick aus „Alle“ heraus setzt die Auswahl
 * darum auf genau diesen Eintrag.
 *
 * Das letzte Häkchen abzuwählen führt zurück zu „Alle“ — der Klick verpufft nicht. Weil das eine
 * Aufhebung ist und keine Verkleinerung, sagt ein Hinweis unter dem Feld, dass der Fokus damit offen
 * steht (#1225).
 */
function MultiSelect(properties: {
    label: string;
    allLabel: string;
    emptyLabel: string;
    /** Was dasteht, wenn das letzte Häkchen abgewählt wurde und der Fokus damit offen steht. */
    focusOpenedLabel: string;
    options: MultiSelectOption[];
    selectedKeys: string[];
    onChange: (keys: string[]) => void;
}) {
    const [open, setOpen] = useState(false);
    // Ob das letzte Häkchen gerade abgewählt wurde. Der Ausleseknopf sagt danach „Alle“ — dass das
    // mehr erfasst als die eine abgewählte Option, sagt erst der Hinweis.
    const [focusOpenedNoted, setFocusOpenedNoted] = useState(false);
    const labelId = useId();

    const selection = selectionFromRule(properties.selectedKeys);
    const allSelected = isAllSelected(selection);

    const toggleAll = () => {
        setFocusOpenedNoted(false);
        properties.onChange([]);
    };

    const toggleOption = (key: string) => {
        // Aus „Alle“ heraus heißt ein Klick „nur dieser“ und nicht „alle außer diesem“: Die
        // Einzelkästchen sind hier leer, und wer eines ankreuzt, schränkt ein.
        if (allSelected) {
            setFocusOpenedNoted(false);
            properties.onChange([key]);

            return;
        }

        const updatedKeys = selection.includes(key)
            ? selection.filter((candidate) => candidate !== key)
            : [...selection, key];

        // Das letzte Häkchen abgewählt: Das ist wieder „Alle“, beim Server also „ohne
        // Einschränkung“ — mehr als die eine Option, die gerade weggeklickt wurde, darum der Hinweis.
        setFocusOpenedNoted(updatedKeys.length === 0);
        // Unverändert weitergereicht, auch wenn die Liste am Ende jede Option nennt: Das ist eine
        // Einschränkung auf genau diese Ids und darf nicht zur leeren Liste werden.
        properties.onChange(updatedKeys);
    };

    const triggerText = allSelected
        ? properties.allLabel
        : properties.options
            .filter((option) => selection.includes(option.key))
            .map((option) => option.text)
            .join(', ');

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Label id={labelId}>{properties.label}</Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-labelledby={labelId}
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        <span className="truncate">{triggerText}</span>
                        <ChevronsUpDown aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                        <CommandList>
                            <CommandEmpty>{properties.emptyLabel}</CommandEmpty>
                            <CommandGroup>
                                <CommandItem value={properties.allLabel} className="gap-2" onSelect={toggleAll}>
                                    <Checkbox checked={allSelected} tabIndex={-1} className="pointer-events-none" />
                                    <span>{properties.allLabel}</span>
                                </CommandItem>
                                {properties.options.map((option) => (
                                    <CommandItem
                                        key={option.key}
                                        value={option.text}
                                        className="gap-2"
                                        onSelect={() => toggleOption(option.key)}
                                    >
                                        <Checkbox
                                            checked={selection.includes(option.key)}
                                            tabIndex={-1}
                                            className="pointer-events-none"
                                        />
                                        <span className="min-w-0 break-words">{option.text}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {focusOpenedNoted && (
                <p role="status" className="text-xs text-muted-foreground">
                    {properties.focusOpenedLabel}
                </p>
            )}
        </div>
    );
}

/** One numbered block of the editor: a short title, then its fields. */
function EditorSection(properties: { number: number; title: string; inline?: boolean; children: ReactNode }) {
    return (
        <section
            className={cn(
                'flex min-w-0 gap-3 rounded-xl border bg-card p-4 text-card-foreground shadow-sm',
                properties.inline ? 'flex-wrap items-center gap-x-6' : 'flex-col',
            )}
        >
            <h2
                className="flex items-center gap-2 text-base font-semibold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]"
            >
                <span
                    aria-hidden
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold"
                >
                    {properties.number}
                </span>
                {properties.title}
            </h2>
            {properties.children}
        </section>
    );
}

function AlertPanel(properties: AlertPanelProperties) {
    const { t } = properties;
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError, showConfirmationDialog } = useModalContext() as any;
    const fieldId = useId();
    const [conditions, setConditions] = useState(properties.alert.conditions || []) as any;
    const [actions, setActions] = useState(properties.alert.actions || []) as any;
    const [_products, setProducts] = useState([]) as any;
    const [productOptions, setProductOptions] = useState<MultiSelectOption[]>([]);
    const [_projects, setProjects] = useState([]) as any;
    const [projectOptions, setProjectOptions] = useState<MultiSelectOption[]>([]);
    const [selectedProducts, setSelectedProducts] = useState([]) as any;
    const [selectedProjects, setSelectedProjects] = useState([]) as any;
    const [name, setName] = useState(properties.alert.name || '') as any;
    const [description, setDescription] = useState(properties.alert.description || '') as any;
    const initialCooldown = cooldownToFields(cooldownMinutesOf(properties.alert));
    const [cooldownAmount, setCooldownAmount] = useState(initialCooldown.amount);
    const [cooldownUnit, setCooldownUnit] = useState<CooldownUnit>(initialCooldown.unit);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState<number | null>(null);
    // Ob schon einmal gespeichert werden sollte. Die Meldung über dem Knopf wird daraus und aus den
    // Aktionen berechnet, nicht gespeichert: Sonst bliebe sie stehen, nachdem das Feld stimmt.
    const [saveAttempted, setSaveAttempted] = useState(false);
    // Ob der Projektfokus in dieser Sitzung von Hand geändert wurde — siehe das Kürzen der
    // Produktauswahl unten.
    const projectsChangedByUser = useRef(false);
    // Die Namen der Produkte, die schon einmal Option waren. Ein herausgefallenes Produkt hat keine
    // Option mehr, soll im Hinweis aber beim Namen genannt werden und nicht als Mongo-Id.
    const productNames = useRef(new Map<string, string>());
    // Dieselbe Auswahl noch einmal als Ref: Der Produkteffekt liest sie **nach** einem `await`, und
    // der Zustand aus seiner Abschlussumgebung wäre dort der von vor dem Klick.
    const selectedProductsRef = useRef<string[]>([]);
    // Welche Produkte beim letzten Projektwechsel aus dem Fokus gefallen sind — beim Namen, damit
    // der Hinweis darunter sie nennen kann.
    const [droppedProducts, setDroppedProducts] = useState<string[]>([]);

    const applySelectedProducts = (keys: string[]) => {
        selectedProductsRef.current = keys;
        setSelectedProducts(keys);
    };

    const nameId = `${fieldId}-name`;
    const descriptionId = `${fieldId}-description`;
    const cooldownId = `${fieldId}-cooldown`;
    const cooldownErrorId = `${fieldId}-cooldown-error`;

    useEffect(() => {
        const call = async () => {
            try {
                const newProjects = authentication?.customer
                    ? await userAPI.query(
                          from(Project.type).where(is('_tenant').equal(authentication.customer)),
                      )
                    : [];

                setProjects(newProjects);
                setProjectOptions(newProjects.map((project: Project) => ({
                    // `_id` ist im Modell optional, bei einem Query-Ergebnis aber immer gesetzt.
                    key: project._id as string,
                    text: project.name,
                })));
                // Bestandsregeln tragen neben den Ids noch das abgelegte Sentinel `_all` (#1225);
                // angezeigt wird, was der Server auch wertet.
                setSelectedProjects(selectionFromRule(properties.alert.projects));
                applySelectedProducts(selectionFromRule(properties.alert.products));
            } catch (error) {
                pushError(error);
            }
        };

        call();
    }, [properties.alert, authentication?.customer]);

    // The product options follow the focused projects: an alert may only target products
    // that are actually deployed there. Resolved server-side (Project → sites → assets →
    // distinct product) because the Product collection is the global catalogue and far too
    // large to load as options. No project selected means "all projects of the tenant".
    useEffect(() => {
        let cancelled = false;

        const call = async () => {
            if (!authentication?.customer) {
                return;
            }

            try {
                const newProducts = await userAPI.getProductsOfProjects(selectionFromRule(selectedProjects));

                if (cancelled) {
                    return;
                }

                setProducts(newProducts);
                setProductOptions(newProducts.map((product: any) => ({ key: product._id, text: product.name })));

                // Ein Produkt, das in den gewählten Projekten nicht (mehr) verbaut ist, hat keine
                // Option mehr: Es stünde in keinem Kästchen, würde aber mitgespeichert. Gekürzt wird
                // nur, wenn der Fokus gerade von Hand geändert wurde — beim Öffnen einer Regel
                // bleibt stehen, was gespeichert ist, sonst würde allein das Öffnen den Fokus auf
                // „alle Produkte“ aufweiten.
                //
                // Gekürzt wird aber nicht **still**: Läuft die Auswahl dabei leer, liest der Server
                // sie als „ohne Einschränkung“ und die Regel feuert für jedes Gerät der Projekte,
                // auch für die ohne Produkt (`matchesProductScope`, tri-server
                // `src/businessRuleScope.ts`) — genau die Aufweitung, gegen die #1181/#1182
                // geschrieben sind. Was herausfällt, steht deshalb unter dem Picker, beim Namen
                // genannt, und dass der Fokus damit offen ist, steht dabei (#1225).
                if (projectsChangedByUser.current) {
                    const available = new Set(newProducts.map((product: any) => product._id));
                    const current = selectionFromRule(selectedProductsRef.current);
                    const dropped = current.filter((key) => !available.has(key));

                    setDroppedProducts(dropped.map((key) => productNames.current.get(key) ?? key));

                    if (dropped.length > 0) {
                        applySelectedProducts(current.filter((key) => available.has(key)));
                    }
                }

                // Erst danach: Bis hierher standen in `productNames` die Namen der alten Optionen,
                // und nur dort steht der Name eines gerade herausgefallenen Produkts noch.
                newProducts.forEach((product: any) =>
                    productNames.current.set(product._id, product.name));
            } catch (error) {
                if (!cancelled) {
                    pushError(error);
                }
            }
        };

        call();

        // A slow response for a superseded project selection must not overwrite a newer one.
        return () => {
            cancelled = true;
        };
    }, [selectedProjects, authentication?.customer]);

    const removeAction = (action: Action) => {
        setActions(actions.filter((entry: Action) => action !== entry));
    };

    const replaceAction = (index: number, newAction: Action) => {
        const newActions = [...actions];

        newActions[index] = newAction;

        setActions(newActions);
    };

    const noUplinkRule = noUplinkDays(conditions) !== null;
    const deviceAlarms = deviceAlarmsOf(conditions);
    const deviceAlarmRule = deviceAlarms !== null;
    // A real alert names the one alarm that fired; the sample shows the one chosen last (else the first).
    const [lastChosenAlarm, setLastChosenAlarm] = useState<string | null>(null);
    const sampleAlarm = deviceAlarms
        ? (lastChosenAlarm !== null && deviceAlarms.includes(lastChosenAlarm) ? lastChosenAlarm : deviceAlarms[0])
        : undefined;
    const addEmailReference = useRef<HTMLButtonElement>(null);
    const [moreOptionsOpen, setMoreOptionsOpen] = useState(cooldownAmount !== '');

    const hasPushAction = (actions as Action[]).some((action) => action instanceof PushNotification);
    // Loaded only while a push action needs it: the editor of an e-mail/SMS rule never asks for it.
    const pushUsers = usePushUsers(authentication?.customer, hasPushAction);
    const ownRoles = useOwnRoles(authentication?.customer, hasPushAction);
    // The test push goes to the signed-in user's own devices: only a user who receives push can try it.
    const ownPush = ownPushStateOf(pushUsers, authentication);

    // SMS cannot be added any more; rules that still carry an SMS action keep it until it is removed.
    const addAction = (kind: 'email' | 'push') =>
        setActions([...actions, defaultAction(kind, noUplinkRule, t, deviceAlarmRule)]);

    /** Removing a notification with content asks first; an empty card goes straight away. */
    const requestRemoveAction = (action: any) => {
        const finish = () => {
            removeAction(action);
            // The card that held the focus is gone: continue at the add buttons.
            window.setTimeout(() => addEmailReference.current?.focus(), 250);
        };

        if (actionIsEmpty(action)) {
            finish();

            return;
        }

        if (action instanceof PushNotification) {
            showConfirmationDialog(
                t(`${TEXT}push.removeTitle`),
                t(`${TEXT}push.removeMessage`),
                finish,
                { confirmText: t(`${TEXT}remove`), destructive: true },
            );

            return;
        }

        const isEmail = action instanceof EmailNotification;
        const recipient = String((isEmail ? action.to : action.phoneNumber) ?? '').trim() || t(`${TEXT}noRecipientYet`);

        showConfirmationDialog(
            t(`${TEXT}${isEmail ? 'removeEmailTitle' : 'removeSmsTitle'}`),
            t(`${TEXT}${isEmail ? 'removeEmailMessage' : 'removeSmsMessage'}`, { to: recipient }),
            finish,
            { confirmText: t(`${TEXT}remove`), destructive: true },
        );
    };

    /** Testversand — gerendert wie der Alarm selbst, mit den Beispieldaten. */
    const sendTest = async (action: any, index: number) => {
        setTesting(index);
        try {
            if (action instanceof EmailNotification) {
                // The sample data is not a real alarm: mark the mail as a test in subject and footer.
                const context = alertSampleContext(properties.language, sampleAlarm);
                await userAPI.sendEmail({
                    to: action.to.split(','),
                    subject: `${t(`${TEXT}testSubjectPrefix`)} ${renderAlertTemplate(action.subject, context)}`,
                    text: `${renderAlertTemplate(action.content, context)}\n\n${t(`${TEXT}testFooter`)}`,
                });
                toast.success(t(`${TEXT}emailSent`));
            } else if (action instanceof PushNotification) {
                // The test goes to the signed-in user's own devices, never to the rule's recipients.
                const context = alertSampleContext(properties.language, sampleAlarm);
                // The title is plain text (no placeholders), so it is sent as it is.
                const title = (action.title ?? '').trim() === '' ? name : action.title;
                const response = await sendTestPush({
                    title: String(title ?? ''),
                    body: renderAlertTemplate(action.content, context),
                });

                if (response.devices === 0) {
                    toast.error(t(`${TEXT}push.testNoDevice`, { appName: BRAND.appName }));
                } else if (response.results.some((result) => result.status === 'error')) {
                    toast.error(t(`${TEXT}push.testFailed`));
                } else {
                    toast.success(t(`${TEXT}push.testSent`, { n: String(response.devices) }));
                }
            } else {
                await userAPI.sendSMS(action.phoneNumber, renderAlertTemplate(action.content, alertSampleContext(properties.language, sampleAlarm)));
                toast.success(t(`${TEXT}messageSent`));
            }
        } catch (error) {
            // Reported, not swallowed. A test send that fails silently is a button that does
            // nothing — which is how the SMS test was described, while the actual causes (a fixed
            // test recipient on the server, and an unusable number) both had something to say.
            pushError(error);
        } finally {
            setTesting(null);
        }
    };
    // Der erste unbekannte Platzhalter über alle Aktionen. Ein unbekannter Platzhalter rendert beim
    // Alarm still als Lücke — das ist #1183, und es fällt erst auf, wenn die Nachricht raus ist.
    const blockingPlaceholder = (actions as any[]).reduce(
        (found: UnknownPlaceholder[], action: any) => found.concat(unknownPlaceholdersOfAction(action)),
        [] as UnknownPlaceholder[],
    )[0];
    // Beim Rendern berechnet: Sobald der Platzhalter im Feld stimmt, ist die Meldung weg — und nicht
    // erst beim nächsten Klick auf „Speichern“.
    const saveBlocked = saveAttempted && blockingPlaceholder
        ? unknownPlaceholderMessage(blockingPlaceholder, t)
        : null;

    // Aus dem Fokus gefallen **und** nichts mehr übrig: Die leere Liste ist beim Server keine
    // Auswahl, sondern der Verzicht auf jede Einschränkung — das ist mehr als ein Nebensatz wert.
    const productFocusWidened = droppedProducts.length > 0 && isAllSelected(selectedProducts);

    // Required text fields: shown as soon as saving was tried, gone as soon as the field is filled.
    const nameMissing = name.trim() === '';
    const descriptionMissing = description.trim() === '';

    // Wait time between alerts (#1488). Not for "Tage ohne Uplink": that rule paces itself by its threshold.
    const cooldown = parseCooldown(cooldownAmount, cooldownUnit);
    const showCooldown = noUplinkDays(conditions) === null;

    const saveAlert = async () => {
        setSaveAttempted(true);

        if (
            nameMissing ||
            descriptionMissing ||
            (showCooldown && cooldown.invalid)
        ) {
            return;
        }

        if (blockingPlaceholder) {
            toast.error(unknownPlaceholderMessage(blockingPlaceholder, t));

            return;
        }

        // A push action without recipients or text notifies nobody: stay in the editor, where its hint is shown.
        if ((actions as Action[]).some((action) => action instanceof PushNotification && !action.isCompletelyDefined())) {
            return;
        }

        setSaving(true);
        try {
            const payload = withAlertLanguage(withPortalRootURL(withCooldownMinutes(new BusinessRule({
                ...properties.alert,
                name,
                // Ohne das abgelegte Sentinel. „Alle“ steht hier schon als leere Liste — keine
                // Einschränkung, auch für das Produkt, das es morgen erst gibt; eine Aufzählung
                // bleibt dagegen eine Aufzählung, auch wenn sie heute alles nennt (#1225).
                products: selectionForSave(selectedProducts),
                projects: selectionForSave(selectedProjects),
                description,
                conditions,
                // "Kritischer Alarm" (SMS escalation) cannot be chosen any more, so no push action leaves the editor
                // with it on — also not one the user did not touch (an older rule saved only for its name).
                actions: (actions as Action[]).map((action) =>
                    action instanceof PushNotification && action.critical
                        ? Action.unmarshal({ ...(action as any), critical: false })
                        : action,
                ),
                // Saving a new no-uplink rule switches the check on (the server would otherwise store it
                // as stopped). Sent explicitly so the server never has to guess; an existing rule keeps
                // its state (the list's Aktivieren/Deaktivieren button changes it).
                ...(noUplinkDays(conditions) !== null && !properties.alert._id
                    ? { status: BusinessRuleRuntimeStatus.running }
                    : {}),
            // tri-model's class drops this field, so it is attached after construction. `null` clears
            // it on the server (back to the default); a no-uplink rule never carries one.
            }), showCooldown ? cooldown.minutes : null)), properties.language);

            if (properties.alert._id) {
                await userAPI.updateBusinessRule(authentication.customer, payload);
            } else {
                await userAPI.createBusinessRule(authentication.customer, payload);
            }

            toast.success(t(`${TEXT}alertSaved`));
            properties.setAlert(null);
        } catch (error) {
            pushError(error);
        } finally {
            setSaving(false);
        }
    };

    const sentence = buildSentence({
        t,
        language: properties.language,
        conditions,
        actions,
        scope: scopeLabel(
            { projects: selectionFromRule(selectedProjects).length || null, products: selectionFromRule(selectedProducts).length || null },
            t,
        ),
        cooldownAmount,
        cooldownUnit,
    });

    return <div className="flex min-w-0 flex-col gap-4">
        <StickyHeader className="flex flex-wrap items-center gap-3 border-b bg-background/95 py-3 backdrop-blur">
            <h3 className="min-w-0 flex-1 break-words text-lg font-semibold">
                {name || t(`${TEXT}newAlert`)}
            </h3>
            <div className="flex gap-2 max-sm:w-full">
                <Button variant="outline" className="max-sm:flex-1" onClick={() => properties.setAlert(null)}>
                    {t(`${TEXT}cancel`)}
                </Button>
                <Button className="max-sm:flex-1" disabled={saving} onClick={saveAlert}>
                    {saving && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                    {saving
                        ? t(`${TEXT}saving`)
                        : properties.alert._id
                            ? t(`${TEXT}save`)
                            : t(`${TEXT}add`)}
                </Button>
            </div>
            {saveBlocked && (
                <p role="alert" className="w-full text-sm text-destructive">
                    {saveBlocked}
                </p>
            )}
        </StickyHeader>
        <AlertSentence text={sentence} />
        <EditorSection number={1} title={t(`${TEXT}sectionBasics`)}>
            <div className="grid min-w-0 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={nameId}>{t(`${TEXT}name`)}</Label>
                    <Input
                        id={nameId}
                        value={name}
                        required
                        aria-invalid={saveAttempted && nameMissing}
                        aria-describedby={saveAttempted && nameMissing ? `${nameId}-error` : undefined}
                        onChange={(event) => setName(event.target.value)}
                    />
                    {saveAttempted && nameMissing && (
                        <p id={`${nameId}-error`} role="alert" className="text-xs text-destructive">
                            {t(`${TEXT}nameRequired`)}
                        </p>
                    )}
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor={descriptionId}>{t(`${TEXT}description`)}</Label>
                    <Input
                        id={descriptionId}
                        value={description}
                        required
                        aria-invalid={saveAttempted && descriptionMissing}
                        aria-describedby={saveAttempted && descriptionMissing ? `${descriptionId}-error` : undefined}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                    {saveAttempted && descriptionMissing && (
                        <p id={`${descriptionId}-error`} role="alert" className="text-xs text-destructive">
                            {t(`${TEXT}descriptionRequired`)}
                        </p>
                    )}
                </div>
            </div>
        </EditorSection>
        <EditorSection number={2} title={t(`${TEXT}sectionWhen`)}>
            {noUplinkIsCombined(conditions) && (
                <p role="status" className="text-sm text-amber-600">
                    {t(`${TEXT}noUplinkAlone`)}
                </p>
            )}
            <AlertConditionsEditor
                t={t}
                language={properties.language}
                conditions={conditions}
                onChange={setConditions}
                onAlarmChosen={setLastChosenAlarm}
            />
            <div className="flex min-w-0 flex-col gap-2">
                <p className="text-sm font-medium">{t(`${TEXT}scope`)}</p>
                <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                    <MultiSelect
                        label={t(`${TEXT}produkte`)}
                        allLabel={t(`${TEXT}alle`)}
                        emptyLabel={t(`${TEXT}noProducts`)}
                        focusOpenedLabel={t(`${TEXT}focusOpenedByEmptySelection`)}
                        options={productOptions}
                        selectedKeys={selectedProducts || []}
                        onChange={(keys) => {
                            // Von Hand angekreuzt: Der Hinweis über die herausgefallenen Produkte ist
                            // damit beantwortet.
                            setDroppedProducts([]);
                            applySelectedProducts(keys);
                        }}
                    />
                    <MultiSelect
                        label={t(`${TEXT}projects`)}
                        allLabel={t(`${TEXT}alle`)}
                        emptyLabel={t(`${TEXT}noProjects`)}
                        focusOpenedLabel={t(`${TEXT}focusOpenedByEmptySelection`)}
                        options={projectOptions}
                        selectedKeys={selectedProjects || []}
                        onChange={(keys) => {
                            projectsChangedByUser.current = true;
                            setSelectedProjects(keys);
                        }}
                    />
                </div>
                {droppedProducts.length > 0 && (
                    <p
                        role={productFocusWidened ? 'alert' : 'status'}
                        className={cn('text-sm', productFocusWidened ? 'text-destructive' : 'text-muted-foreground')}
                    >
                        {t(`${TEXT}productsOutOfScope`, { products: droppedProducts.join(', ') })}
                        {productFocusWidened ? ` ${t(`${TEXT}focusCoversEveryProduct`)}` : ''}
                    </p>
                )}
            </div>
        </EditorSection>
        <EditorSection number={3} title={t(`${TEXT}sectionThen`)}>
            {actions.map((action: Action, index: number) => action instanceof PushNotification ? (
                <PushActionEditor
                    key={`action${index}`}
                    t={t}
                    action={action}
                    index={index}
                    fieldId={fieldId}
                    testing={testing === index}
                    ruleName={name}
                    noUplink={noUplinkRule}
                    deviceAlarm={sampleAlarm}
                    deviceAlarms={deviceAlarms ?? undefined}
                    onDeviceAlarmChoose={setLastChosenAlarm}
                    language={properties.language}
                    users={pushUsers}
                    ownPush={ownPush}
                    ownPushChecking={pushUsers.status === 'loading'}
                    ownRoles={ownRoles.roles}
                    onRefreshRecipients={() => {
                        pushUsers.reload?.();
                        ownRoles.reload();
                    }}
                    showErrors={saveAttempted}
                    onChange={(updated: Action) => replaceAction(index, updated)}
                    onRemove={() => requestRemoveAction(action)}
                    onTest={() => sendTest(action, index)}
                />
            ) : (
                <ActionEditor
                    key={`action${index}`}
                    t={t}
                    action={action}
                    index={index}
                    fieldId={fieldId}
                    testing={testing === index}
                    noUplink={noUplinkRule}
                    deviceAlarm={sampleAlarm}
                    deviceAlarms={deviceAlarms ?? undefined}
                    onDeviceAlarmChoose={setLastChosenAlarm}
                    language={properties.language}
                    showErrors={saveAttempted}
                    onChange={(updated: Action) => replaceAction(index, updated)}
                    onRemove={() => requestRemoveAction(action)}
                    onTest={() => sendTest(action, index)}
                />
            ))}
            <div
                data-guide-id="settings.alerts.addAction"
                className={cn('grid min-w-0 gap-3', actions.length === 0 ? 'sm:grid-cols-2' : 'sm:flex sm:flex-wrap')}
            >
                <Button
                    data-guide-id="settings.alerts.addPush"
                    variant="outline"
                    className={cn('gap-2', actions.length === 0 && 'h-16 text-base')}
                    onClick={() => addAction('push')}
                >
                    <Bell aria-hidden className="h-5 w-5" />
                    {t(`${TEXT}addPushAction`)}
                </Button>
                <Button
                    ref={addEmailReference}
                    variant="outline"
                    className={cn('gap-2', actions.length === 0 && 'h-16 text-base')}
                    onClick={() => addAction('email')}
                >
                    <Mail aria-hidden className="h-5 w-5" />
                    {t(`${TEXT}addEmailAction`)}
                </Button>
            </div>
            {actions.length === 0 && (
                <p className="text-sm text-muted-foreground">{t(`${TEXT}noActionsHint`)}</p>
            )}
        </EditorSection>
        {showCooldown && (
            <div className="flex min-w-0 flex-col gap-2">
                <button
                    type="button"
                    className="inline-flex items-center gap-1 self-start rounded-md px-1 py-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:min-h-11"
                    aria-expanded={moreOptionsOpen}
                    aria-controls={`${fieldId}-more`}
                    onClick={() => setMoreOptionsOpen(!moreOptionsOpen)}
                >
                    <ChevronRight aria-hidden className={cn('h-4 w-4 transition-transform', moreOptionsOpen && 'rotate-90')} />
                    {t(`${TEXT}moreOptions`)}
                </button>
                {moreOptionsOpen && (
                    <div
                        id={`${fieldId}-more`}
                        className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border bg-card p-4 shadow-sm"
                    >
                        <span aria-hidden className="text-sm">{t(`${TEXT}waitLead`)}</span>
                        <Label htmlFor={cooldownId} className="sr-only">
                            {t(`${TEXT}cooldown`)}
                        </Label>
                        <Input
                            id={cooldownId}
                            type="number"
                            inputMode="numeric"
                            min={1}
                            className="w-40"
                            value={cooldownAmount}
                            placeholder={t(`${TEXT}cooldownDefault`)}
                            aria-invalid={cooldown.invalid}
                            aria-describedby={cooldown.invalid ? cooldownErrorId : undefined}
                            onChange={(event) => setCooldownAmount(event.target.value)}
                        />
                        <select
                            aria-label={t(`${TEXT}cooldownUnit`)}
                            className="h-9 max-sm:h-11 rounded-md border border-input bg-background px-3 text-sm"
                            value={cooldownUnit}
                            onChange={(event) => setCooldownUnit(event.target.value as CooldownUnit)}
                        >
                            <option value="minutes">{t(`${TEXT}cooldownMinutes`)}</option>
                            <option value="hours">{t(`${TEXT}cooldownHours`)}</option>
                        </select>
                        {cooldown.invalid && (
                            <p id={cooldownErrorId} role="alert" className="w-full text-xs text-destructive">
                                {t(`${TEXT}cooldownInvalid`)}
                            </p>
                        )}
                    </div>
                )}
            </div>
        )}
    </div>;
}

type StatusKind = 'running' | 'stopped' | 'interrupted' | 'unknown';

/** The runtime state of a rule as the list understands it; anything else is "unknown". */
const statusOf = (alert: any): StatusKind => {
    const status = alert?.status;

    if (status === BusinessRuleRuntimeStatus.running || status === 'running') {
        return 'running';
    }

    if (status === BusinessRuleRuntimeStatus.stopped || status === 'stopped') {
        return 'stopped';
    }

    if (status === BusinessRuleRuntimeStatus.interrupted || status === 'interrupted') {
        return 'interrupted';
    }

    return 'unknown';
};

const STATUS_FILTERS: ('all' | StatusKind)[] = ['all', 'running', 'stopped', 'interrupted', 'unknown'];

const STATUS_PILL: Record<StatusKind, string> = {
    running: 'border-success/30 bg-success/10 text-success',
    stopped: 'border-transparent bg-muted text-muted-foreground',
    interrupted: 'border-warning/30 bg-warning/10 text-warning',
    unknown: 'border-dashed bg-transparent text-muted-foreground',
};

const STATUS_DOT: Record<StatusKind, string> = {
    running: 'bg-success',
    stopped: 'bg-muted-foreground/60',
    interrupted: 'bg-warning',
    unknown: 'bg-muted-foreground/40',
};

export default function AlertManagementPanel(_properties: Properties) {
    const { t, i18n } = useTranslation();
    // Einmal hier festgehalten, was `TFunction` mit Werten zurückgibt (siehe `Translate`), statt an
    // jeder Aufrufstelle im Editor.
    const translate: Translate = (key, values) => t(key, values) as string;
    const language = (i18n?.language ?? 'de').slice(0, 2);
    const { page, onPageChange } = useUrlPageState('alertListPage');
    const { showDeleteDialog, showConfirmationDialog, pushError } = useModalContext() as any;
    const [authentication] = useAtom(authenticationAtom) as any;
    const [alerts, setAlerts] = useState<BusinessRule[]>([]);
    const [selectedAlert, setSelectedAlert] = useState() as any;
    /**
     * How often the editor has been opened — the `key` of `AlertPanel`.
     *
     * `AlertPanel` copies `properties.alert` into its own state on **mount** (name,
     * description, conditions, actions). If an open editor already sits at the same place in
     * the tree, a new `alert` is just a new prop: React does not remount, the old state stays —
     * and because the **new** rule's `_id` is `undefined`, saving created a copy of the rule
     * edited **before**. Reachable via prefill (`useGuideAction` fires on every new request in
     * the mounted panel) and via the existing path "from rule A directly to rule B". A counter
     * in the `key` binds the editor to the act of opening and fixes both cases.
     */
    const [editorGeneration, setEditorGeneration] = useState(0);
    const [nextRefresh, setNextRefresh] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [togglingAlert, setTogglingAlert] = useState<string | null>(null);
    const [choosing, setChoosing] = useState(false);
    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | StatusKind>('all');

    /** The only way into the editor: sets the rule **and** increments the `key`. */
    const openAlertEditor = (alert: BusinessRule) => {
        setChoosing(false);
        setEditorGeneration((generation) => generation + 1);
        setSelectedAlert(alert);
    };

    /**
     * Prefill from the address (`?panel=alerts&guide=settings.alerts.create&name=…&description=…`):
     * the editor opens with name and description set; saving only happens on click.
     *
     * The conditions stay empty: `conditions` is a list of comparisons built via a selection
     * menu — not settable via the address (see `guideAction.fields` of the registry entry
     * `settings.alerts.action.add`).
     */
    useGuideAction(
        'settings.alerts.create',
        (values) => openAlertEditor(new BusinessRule({ name: values.name, description: values.description })),
        { panel: 'alerts' },
    );

    const loadRuntimes = async () => {
        setRefreshing(true);

        try {
            const alerts = await userAPI.getBusinessRules(authentication.customer);

            setAlerts(alerts || []);
        } finally {
            setRefreshing(false);
            setInitialLoading(false);
            setNextRefresh(60);
        }
    };

    /** Ein Zeitpunkt, den es noch nicht gibt: „–", und am Zeiger steht, warum. */
    const pendingDateTime = (value: any) => {
        if (value) {
            return <span className="whitespace-nowrap text-sm">{formatDateTime(value)}</span>;
        }

        const hint = t(`${TEXT}notAvailableYet`);

        return (
            <span
                // Ein `aria-label` trägt nur ein Element mit Rolle; auf einem blanken `span`
                // überlesen es die meisten Screenreader, und der Grund bliebe ein reiner Tooltip.
                role="img"
                className="whitespace-nowrap text-sm text-muted-foreground"
                title={hint}
                aria-label={hint}
            >
                –
            </span>
        );
    };

    const toggleAlert = async (alert: any, stopped: boolean) => {
        setTogglingAlert(alert._id || alert.id);
        try {
            if (stopped) {
                await userAPI.activateBusinessRule(authentication.customer, alert);
            } else {
                await userAPI.deactivateBusinessRule(authentication.customer, alert);
            }
            await loadRuntimes();
        } catch (error) {
            pushError(error);
        } finally {
            setTogglingAlert(null);
        }
    };

    const words = { and: t(`${TEXT}summaryAnd`), or: t(`${TEXT}summaryOr`), operators: operatorWords(translate) };

    /** "Druck < 10 bar · 2 Empfänger · Wartezeit 2 Min." — what the rule does, in one line. */
    const ruleSummary = (alert: any): string => {
        const days = noUplinkDays(alert.conditions);
        const waitMinutes = cooldownMinutesOf(alert);
        const recipients = recipientCount(alert.actions);
        const parts: string[] = [];

        if (days !== null) {
            parts.push(t(`${TEXT}noUplinkSummary`, { days: String(days ?? '?') }));
        } else {
            const text = summarizeConditions(alert.conditions, language, words);

            if (text !== '') {
                parts.push(text);
            }
        }

        parts.push(
            (alert.actions ?? []).length === 0
                ? t(`${TEXT}noAction`)
                : recipients === 1
                    ? t(`${TEXT}recipientOne`)
                    : t(`${TEXT}recipients`, { n: String(recipients) }),
        );

        if (waitMinutes !== null && days === null) {
            parts.push(t(`${TEXT}cooldownSummary`, {
                value: cooldownToFields(waitMinutes).unit === 'hours'
                    ? `${waitMinutes / 60} ${t(`${TEXT}cooldownHoursShort`)}`
                    : `${waitMinutes} ${t(`${TEXT}cooldownMinutesShort`)}`,
            }));
        }

        return parts.join(' · ');
    };

    const scopeText = (alert: any): string => scopeLabel(scopeCountsOf(alert), translate);

    const columns: ColumnDef<any>[] = [{
        id: 'status',
        header: () => <span className="font-bold text-foreground">{t(`${TEXT}status`)}</span>,
        enableSorting: false,
        // Icon plus visible text, and nothing that moves. The running state used to be a spinning
        // loader, which a customer read as "deleting in progress" while the row simply stayed (#1184).
        cell: ({ row }) => {
            const kind = statusOf(row.original);
            const inactiveNoUplink = kind === 'stopped' && noUplinkDays(row.original.conditions) !== null;
            const label = kind === 'running'
                ? t(`${TEXT}running`)
                : kind === 'stopped'
                    ? inactiveNoUplink ? t(`${TEXT}noUplinkInactive`) : t(`${TEXT}stopped`)
                    : kind === 'interrupted'
                        ? t(`${TEXT}interrupted`)
                        : t(`${TEXT}unknown`);

            return <span
                className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
                    STATUS_PILL[kind],
                )}
                title={inactiveNoUplink ? t(`${TEXT}noUplinkInactiveHint`) : undefined}
            >
                <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT[kind])} />
                {label}
            </span>;
        },
    }, {
        id: 'name',
        accessorFn: (alert: any) => (alert.name || '').toLowerCase(),
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t(`${TEXT}name`)}
            </SortableHeader>
        ),
        cell: ({ row }) => <div className="flex min-w-0 flex-col gap-0.5">
            <button
                type="button"
                className="min-w-0 break-words text-left text-sm font-medium hover:underline focus-visible:underline focus-visible:outline-none"
                onClick={(event) => {
                    event.stopPropagation();
                    openAlertEditor(row.original);
                }}
            >
                {row.original.name}
            </button>
            {row.original.description && (
                <span
                    className="block max-w-[48ch] truncate text-xs text-muted-foreground max-sm:max-w-none max-sm:whitespace-normal max-sm:break-words"
                    title={row.original.description}
                >
                    {row.original.description}
                </span>
            )}
            <span className="min-w-0 break-words text-xs text-muted-foreground">{ruleSummary(row.original)}</span>
        </div>,
    }, {
        id: 'scope',
        header: () => <span className="font-bold text-foreground">{t(`${TEXT}scope`)}</span>,
        enableSorting: false,
        meta: { className: 'hidden md:table-cell' },
        cell: ({ row }) => (
            <Badge variant="outline" className="whitespace-nowrap font-normal">{scopeText(row.original)}</Badge>
        ),
    },
    // Der Server schreibt `lastCheck`/`lastExecution` noch nicht (tri-server #1182, wartet auf den
    // Cooldown-Speicher aus #1145). Die Spalte bleibt trotzdem stehen und sagt es: „–" mit dem
    // Grund am Zeiger ist eine Auskunft, eine verschwundene Spalte ist keine — und ein Kunde, der
    // die Zeile sucht, sucht dann nach einem Fehler, den es nicht gibt (#1225). Aktivierung und
    // letzte Prüfung stehen als Zeile darunter, solange es sie gibt.
    {
        id: 'lastExecution',
        accessorFn: (alert: any) => (alert.lastExecution ? new Date(alert.lastExecution).getTime() : 0),
        header: ({ column }) => (
            <SortableHeader column={column}>
                {t(`${TEXT}lastExecution`)}
            </SortableHeader>
        ),
        meta: { className: 'hidden lg:table-cell' },
        cell: ({ row }) => <div className="flex flex-col gap-0.5">
            {pendingDateTime(row.original.lastExecution)}
            {row.original.lastCheck && (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {t(`${TEXT}lastCheck`)}: {formatDateTime(row.original.lastCheck)}
                </span>
            )}
            {row.original.activation && (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {t(`${TEXT}activation`)}: {formatDateTime(row.original.activation)}
                </span>
            )}
        </div>,
    }, {
        id: 'actions',
        header: () => <span className="sr-only">{t(`${TEXT}actions`)}</span>,
        enableSorting: false,
        cell: ({ row }) => {
            const alert = row.original;
            const stopped = alert.status === BusinessRuleRuntimeStatus.stopped || alert.status === 'stopped';
            const busy = togglingAlert === (alert._id || alert.id);
            const toggleLabel = stopped ? t(`${TEXT}activate`) : t(`${TEXT}deactivate`);
            const ToggleIcon = stopped ? Power : PowerOff;

            return <div className="flex items-center justify-end gap-1">
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={busy}
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    onClick={async (event) => {
                        event.stopPropagation();

                        const uplinkDays = noUplinkDays(alert.conditions);

                        if (stopped && uplinkDays !== null) {
                            // Switching a no-uplink rule on starts alarms for the whole scope.
                            showConfirmationDialog(
                                t(`${TEXT}noUplinkActivateTitle`),
                                t(`${TEXT}noUplinkActivateConfirm`, { days: String(uplinkDays ?? '?') }),
                                () => { void toggleAlert(alert, stopped); },
                            );

                            return;
                        }

                        await toggleAlert(alert, stopped);
                    }}
                >
                    {busy
                        ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                        : <ToggleIcon aria-hidden className="h-4 w-4" />}
                    <span className="max-lg:hidden">{toggleLabel}</span>
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t(`${TEXT}deleteAlert`)}
                    title={t(`${TEXT}deleteAlert`)}
                    onClick={(event) => {
                        event.stopPropagation();
                        // The dialog awaits this and reports a rejection (#1184). The list is re-read
                        // through GET /business-rules — the same tenant-keyed cache the server deletes
                        // from and the periodic refresh reads — not through the generic query, which
                        // does not agree with it about what the customer has.
                        showDeleteDialog(t(`${TEXT}deleteAlert`), alert.name, async () => {
                            await userAPI.deleteBusinessRule(authentication.customer, alert);
                            await loadRuntimes();
                        });
                    }}
                >
                    <Trash2 aria-hidden className="h-4 w-4" />
                </Button>
            </div>;
        },
    }];

    useEffect(() => {
        loadRuntimes();

        const interval = setInterval(async () => {
            const newNextRefresh = nextRefresh - 5;

            if (newNextRefresh <= 0) {
                await loadRuntimes();
            } else {
                setNextRefresh(newNextRefresh);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [nextRefresh]);

    // A new alert starts from a common case; editing and the Tri-Sense prefill go straight to the editor.
    const createAlert = () => {
        setChoosing(true);
    };

    const choosePreset = (preset: AlertPreset) => {
        if (preset.id === 'custom') {
            openAlertEditor(new BusinessRule({}));

            return;
        }

        const noUplink = preset.id === 'noUplink';

        const rule = new BusinessRule({
            name: translate(`${TEXT}preset.${preset.id}.name`),
            description: translate(`${TEXT}preset.${preset.id}.description`),
            conditions: preset.conditions,
            actions: [defaultAction('email', noUplink, translate, preset.id === 'deviceAlarm')],
        } as any);

        // The wait time travels beside the model instance (see alertCooldown); only presets that set one.
        openAlertEditor(preset.cooldownMinutes === undefined ? rule : withCooldownMinutes(rule, preset.cooldownMinutes));
    };

    const needle = query.trim().toLowerCase();
    const filterActive = needle !== '' || statusFilter !== 'all';
    const visibleAlerts = alerts.filter((alert: any) =>
        (statusFilter === 'all' || statusOf(alert) === statusFilter)
        && (needle === ''
            || `${alert.name ?? ''} ${alert.description ?? ''} ${ruleSummary(alert)}`.toLowerCase().includes(needle)));

    const resetFilter = () => {
        setQuery('');
        setStatusFilter('all');
        onPageChange(1);
    };

    return <div className={CENTERED_PAGE_CLASS}>
        {choosing && !selectedAlert
            ? <AlertPresetChooser t={translate} onChoose={choosePreset} onCancel={() => setChoosing(false)} />
            : selectedAlert
            ?
            <AlertPanel key={editorGeneration} alert={selectedAlert} setAlert={async (_alert: BusinessRule | null) => {
                setSelectedAlert(null);
                await loadRuntimes();
            }} t={translate} language={language}></AlertPanel>
            :
            <>
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <div className="relative min-w-[180px] max-w-sm flex-1">
                        <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            type="search"
                            className="pl-9"
                            value={query}
                            aria-label={t(`${TEXT}searchAlerts`)}
                            placeholder={t(`${TEXT}searchAlerts`)}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                onPageChange(1);
                            }}
                        />
                    </div>
                    <select
                        aria-label={t(`${TEXT}status`)}
                        className="h-9 rounded-md border border-input bg-background px-3 text-sm max-sm:h-11"
                        value={statusFilter}
                        onChange={(event) => {
                            setStatusFilter(event.target.value as 'all' | StatusKind);
                            onPageChange(1);
                        }}
                    >
                        {STATUS_FILTERS.map((kind) => (
                            <option key={kind} value={kind}>
                                {kind === 'all' ? t(`${TEXT}allStatuses`) : t(`${TEXT}${kind}`)}
                            </option>
                        ))}
                    </select>
                    <div className="ml-auto flex flex-wrap items-center gap-1">
                        <span className="text-xs text-muted-foreground">
                            {refreshing
                                ? t(`${TEXT}refreshing`)
                                : `${t(`${TEXT}nextRefreshIn`)} ${nextRefresh} ${t(`${TEXT}seconds`)}`}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            disabled={refreshing}
                            aria-label={t('global.refresh')}
                            title={t('global.refresh')}
                            onClick={() => loadRuntimes()}
                        >
                            <RefreshCw aria-hidden className={cn('h-4 w-4', refreshing && 'animate-spin')} />
                        </Button>
                        <Button data-guide-id="settings.alerts.create" className="gap-2" onClick={createAlert}>
                            <MessageSquarePlus aria-hidden className="h-4 w-4" />
                            {t(`${TEXT}addAlert`)}
                        </Button>
                    </div>
                </div>
                <div className="min-w-0">
                    <DataTable
                        columns={columns}
                        data={visibleAlerts}
                        loading={initialLoading}
                        pageSize={10}
                        page={page}
                        onPageChange={onPageChange}
                        onRowClick={(alert) => openAlertEditor(alert)}
                        previousAriaLabel={t('global.back')}
                        nextAriaLabel={t('global.next')}
                        emptyMessage={
                            filterActive && alerts.length > 0
                                ? <TableEmptyState
                                    title={t(`${TEXT}noMatches`)}
                                    action={
                                        <Button variant="outline" size="sm" onClick={resetFilter}>
                                            {t(`${TEXT}resetFilter`)}
                                        </Button>
                                    }
                                />
                                : <TableEmptyState
                                    title={t(`${TEXT}noAlerts`)}
                                    hint={t(`${TEXT}noAlertsHint`)}
                                    action={
                                        <Button data-guide-id="settings.alerts.create" variant="outline" size="sm" className="gap-2" onClick={createAlert}>
                                            <Plus aria-hidden className="h-4 w-4" />
                                            {t(`${TEXT}addAlert`)}
                                        </Button>
                                    }
                                />
                        }
                    />
                </div>
            </>
        }
    </div>;
}
